import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  getCompanyRatingSummary, 
  getCompanyRatings, 
  createRating, 
  getRatingCriteria,
  checkExistingRating 
} from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Star, MessageSquare, Loader2, TrendingUp, TrendingDown, User } from 'lucide-react';
import { toast } from 'sonner';

// Star Rating Component
const StarRating = ({ value, onChange, readonly = false, size = "md" }) => {
  const [hoverValue, setHoverValue] = useState(0);
  const sizeClass = size === "sm" ? "w-4 h-4" : size === "lg" ? "w-6 h-6" : "w-5 h-5";
  
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readonly}
          className={`${readonly ? 'cursor-default' : 'cursor-pointer'} transition-colors`}
          onClick={() => !readonly && onChange && onChange(star)}
          onMouseEnter={() => !readonly && setHoverValue(star)}
          onMouseLeave={() => !readonly && setHoverValue(0)}
        >
          <Star 
            className={`${sizeClass} ${
              (hoverValue || value) >= star 
                ? 'fill-yellow-400 text-yellow-400' 
                : 'text-slate-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

// Rating Summary Card
const RatingSummaryCard = ({ summary, type }) => {
  if (!summary || summary.total_ratings === 0) {
    return (
      <div className="text-center py-8 text-slate-500">
        <Star className="w-12 h-12 mx-auto mb-2 text-slate-300" />
        <p>Sin calificaciones {type === 'provider' ? 'como proveedor' : 'como cliente'} aún</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Overall Score */}
      <div className="flex items-center gap-4">
        <div className="text-4xl font-bold" style={{ color: '#1E4C8E' }}>
          {summary.overall_average.toFixed(1)}
        </div>
        <div>
          <StarRating value={Math.round(summary.overall_average)} readonly size="lg" />
          <p className="text-sm text-slate-500 mt-1">
            {summary.total_ratings} calificación{summary.total_ratings !== 1 ? 'es' : ''}
          </p>
        </div>
      </div>

      {/* Criteria Breakdown */}
      <div className="space-y-3">
        {summary.criteria_labels.map((criterion) => {
          const avg = summary.criteria_averages[criterion.key] || 0;
          const percentage = (avg / 5) * 100;
          return (
            <div key={criterion.key} className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">{criterion.label}</span>
                <span className="font-medium">{avg.toFixed(1)}</span>
              </div>
              <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className="h-full rounded-full transition-all"
                  style={{ width: `${percentage}%`, backgroundColor: '#34B772' }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Single Rating Display
const RatingItem = ({ rating }) => {
  return (
    <div className="border-b border-slate-100 last:border-0 py-4">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
            <User className="w-4 h-4 text-slate-500" />
          </div>
          <div>
            <p className="font-medium text-sm">{rating.reviewer_company_name || 'Empresa'}</p>
            <p className="text-xs text-slate-500">
              {new Date(rating.created_at).toLocaleDateString('es-MX', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StarRating value={Math.round(rating.average_rating)} readonly size="sm" />
          <span className="font-semibold text-sm">{rating.average_rating.toFixed(1)}</span>
        </div>
      </div>
      
      {rating.comment && (
        <p className="text-slate-600 text-sm mt-2 pl-10">{rating.comment}</p>
      )}
      
      {rating.transaction_type && (
        <Badge variant="outline" className="text-xs mt-2 ml-10">
          {rating.transaction_type}
        </Badge>
      )}
    </div>
  );
};

// Rating Form
const RatingForm = ({ companyId, companyName, ratingType, criteria, onSuccess, onCancel }) => {
  const [criteriaRatings, setCriteriaRatings] = useState({});
  const [comment, setComment] = useState('');
  const [transactionType, setTransactionType] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all criteria rated
    const missingCriteria = criteria.filter(c => !criteriaRatings[c.key]);
    if (missingCriteria.length > 0) {
      toast.error('Por favor califica todos los criterios');
      return;
    }

    setSubmitting(true);
    try {
      await createRating({
        target_company_id: companyId,
        rating_type: ratingType,
        criteria_ratings: criteriaRatings,
        comment: comment || null,
        transaction_type: transactionType || null
      });
      toast.success('Calificación enviada');
      onSuccess && onSuccess();
    } catch (error) {
      toast.error(error.message || 'Error al enviar calificación');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <p className="text-sm text-slate-600">
        Califica a <strong>{companyName}</strong> como {ratingType === 'provider' ? 'proveedor' : 'cliente'}
      </p>

      {/* Criteria Ratings */}
      <div className="space-y-4">
        {criteria.map((criterion) => (
          <div key={criterion.key} className="flex items-center justify-between">
            <Label className="text-sm">{criterion.label}</Label>
            <StarRating 
              value={criteriaRatings[criterion.key] || 0}
              onChange={(val) => setCriteriaRatings({...criteriaRatings, [criterion.key]: val})}
            />
          </div>
        ))}
      </div>

      {/* Transaction Type */}
      <div className="space-y-2">
        <Label className="text-sm">Tipo de transacción (opcional)</Label>
        <select
          value={transactionType}
          onChange={(e) => setTransactionType(e.target.value)}
          className="w-full rounded-md border border-slate-200 p-2 text-sm"
        >
          <option value="">Seleccionar...</option>
          <option value="cotizacion">Cotización</option>
          <option value="muestra">Muestra física</option>
          <option value="pedido">Pedido</option>
          <option value="importacion">Importación</option>
          <option value="excedente">Compra de excedente</option>
        </select>
      </div>

      {/* Comment */}
      <div className="space-y-2">
        <Label className="text-sm">Comentario (opcional)</Label>
        <Textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Comparte tu experiencia..."
          rows={3}
        />
      </div>

      <div className="flex gap-3">
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
          Cancelar
        </Button>
        <Button 
          type="submit" 
          className="flex-1"
          style={{ backgroundColor: '#34B772' }}
          disabled={submitting}
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enviar calificación'}
        </Button>
      </div>
    </form>
  );
};

// Main Component
const CompanyRatings = ({ companyId, companyName, companyType }) => {
  const { isAuthenticated, user } = useAuth();
  const [providerSummary, setProviderSummary] = useState(null);
  const [buyerSummary, setBuyerSummary] = useState(null);
  const [providerRatings, setProviderRatings] = useState([]);
  const [buyerRatings, setBuyerRatings] = useState([]);
  const [criteria, setCriteria] = useState({ provider: [], buyer: [] });
  const [loading, setLoading] = useState(true);
  const [ratingDialogOpen, setRatingDialogOpen] = useState(false);
  const [selectedRatingType, setSelectedRatingType] = useState('provider');
  const [hasRated, setHasRated] = useState({ provider: false, buyer: false });

  const isOwnCompany = user?.company_id === companyId;

  useEffect(() => {
    fetchData();
  }, [companyId]);

  const fetchData = async () => {
    try {
      const [criteriaData, providerSum, buyerSum, providerRats, buyerRats] = await Promise.all([
        getRatingCriteria(),
        getCompanyRatingSummary(companyId, 'provider'),
        getCompanyRatingSummary(companyId, 'buyer'),
        getCompanyRatings(companyId, 'provider', 0, 10),
        getCompanyRatings(companyId, 'buyer', 0, 10)
      ]);
      
      setCriteria(criteriaData);
      setProviderSummary(providerSum);
      setBuyerSummary(buyerSum);
      setProviderRatings(providerRats);
      setBuyerRatings(buyerRats);

      // Check if current user has already rated
      if (isAuthenticated && user?.company_id) {
        try {
          const [provCheck, buyerCheck] = await Promise.all([
            checkExistingRating(companyId, 'provider'),
            checkExistingRating(companyId, 'buyer')
          ]);
          setHasRated({
            provider: provCheck.has_rated,
            buyer: buyerCheck.has_rated
          });
        } catch (e) {
          // Ignore errors
        }
      }
    } catch (error) {
      console.error('Error fetching ratings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRatingSuccess = () => {
    setRatingDialogOpen(false);
    fetchData();
  };

  const openRatingDialog = (type) => {
    if (!isAuthenticated) {
      toast.error('Debes iniciar sesión para calificar');
      return;
    }
    if (isOwnCompany) {
      toast.error('No puedes calificarte a ti mismo');
      return;
    }
    if (hasRated[type]) {
      toast.info('Ya has calificado esta empresa');
      return;
    }
    setSelectedRatingType(type);
    setRatingDialogOpen(true);
  };

  if (loading) {
    return (
      <Card className="card-industrial">
        <CardContent className="p-6 text-center">
          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        </CardContent>
      </Card>
    );
  }

  // Determine which tabs to show based on company type
  const showProviderTab = companyType === 'proveedor' || companyType === 'ambos';
  const showBuyerTab = companyType === 'fabricante' || companyType === 'ambos';
  const defaultTab = showProviderTab ? 'provider' : 'buyer';

  return (
    <Card className="card-industrial" data-testid="company-ratings">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-500" />
            Referencias comerciales
          </CardTitle>
          {!isOwnCompany && isAuthenticated && (
            <Dialog open={ratingDialogOpen} onOpenChange={setRatingDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  size="sm"
                  style={{ backgroundColor: '#1E4C8E' }}
                  className="hover:opacity-90"
                  data-testid="add-rating-btn"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Calificar
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Calificar empresa</DialogTitle>
                </DialogHeader>
                <Tabs value={selectedRatingType} onValueChange={setSelectedRatingType}>
                  <TabsList className="grid w-full grid-cols-2 mb-4">
                    {showProviderTab && (
                      <TabsTrigger value="provider" disabled={hasRated.provider}>
                        Como proveedor
                      </TabsTrigger>
                    )}
                    {showBuyerTab && (
                      <TabsTrigger value="buyer" disabled={hasRated.buyer}>
                        Como cliente
                      </TabsTrigger>
                    )}
                  </TabsList>
                  
                  {showProviderTab && (
                    <TabsContent value="provider">
                      <RatingForm
                        companyId={companyId}
                        companyName={companyName}
                        ratingType="provider"
                        criteria={criteria.provider}
                        onSuccess={handleRatingSuccess}
                        onCancel={() => setRatingDialogOpen(false)}
                      />
                    </TabsContent>
                  )}
                  {showBuyerTab && (
                    <TabsContent value="buyer">
                      <RatingForm
                        companyId={companyId}
                        companyName={companyName}
                        ratingType="buyer"
                        criteria={criteria.buyer}
                        onSuccess={handleRatingSuccess}
                        onCancel={() => setRatingDialogOpen(false)}
                      />
                    </TabsContent>
                  )}
                </Tabs>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue={defaultTab}>
          <TabsList className="grid w-full grid-cols-2 mb-4">
            {showProviderTab && (
              <TabsTrigger value="provider" className="flex items-center gap-1">
                <TrendingUp className="w-4 h-4" />
                Como proveedor
              </TabsTrigger>
            )}
            {showBuyerTab && (
              <TabsTrigger value="buyer" className="flex items-center gap-1">
                <TrendingDown className="w-4 h-4" />
                Como cliente
              </TabsTrigger>
            )}
          </TabsList>

          {showProviderTab && (
            <TabsContent value="provider">
              <RatingSummaryCard summary={providerSummary} type="provider" />
              {providerRatings.length > 0 && (
                <div className="mt-6 border-t pt-4">
                  <h4 className="font-semibold text-sm mb-3">Últimas calificaciones</h4>
                  {providerRatings.slice(0, 3).map((rating) => (
                    <RatingItem key={rating.rating_id} rating={rating} />
                  ))}
                </div>
              )}
            </TabsContent>
          )}

          {showBuyerTab && (
            <TabsContent value="buyer">
              <RatingSummaryCard summary={buyerSummary} type="buyer" />
              {buyerRatings.length > 0 && (
                <div className="mt-6 border-t pt-4">
                  <h4 className="font-semibold text-sm mb-3">Últimas calificaciones</h4>
                  {buyerRatings.slice(0, 3).map((rating) => (
                    <RatingItem key={rating.rating_id} rating={rating} />
                  ))}
                </div>
              )}
            </TabsContent>
          )}
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default CompanyRatings;
